import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject
} from '@angular/core';
import { MenuItem } from '../interfaces/menu-item.interface';
import { trackById } from '@vex/utils/track-by';
import { VexPopoverRef } from '@vex/components/vex-popover/vex-popover-ref';
import { Router, RouterLink } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { ContextualSidebarService } from 'src/app/shared/services/contextual-sidebar.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'vex-toolbar-user-dropdown',
  templateUrl: './toolbar-user-dropdown.component.html',
  styleUrls: ['./toolbar-user-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatRippleModule,
    RouterLink,
    NgClass,
    TranslatePipe
  ]
})
export class ToolbarUserDropdownComponent {
  private readonly popoverRef = inject(VexPopoverRef<ToolbarUserDropdownComponent>);
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthStore);
  private readonly contextualSidebarService = inject(ContextualSidebarService);
  private readonly vexLayoutService = inject(VexLayoutService);
  private readonly router = inject(Router);

  readonly user = this.authStore.user;
  readonly trackById = trackById;

  readonly userName = computed(() => {
    const u = this.user();
    if (!u) return 'User';
    if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`;
    if (u.firstName) return u.firstName;
    if (u.email) return u.email.split('@')[0];
    return 'User';
  });

  readonly items = computed<MenuItem[]>(() => {
    const u = this.user();

    // #412 — this menu is about the person; everything that belongs to the
    // workspace (team, subscription, billing profiles) lives in the aside's
    // user block. Labels are the aside's keys, so a page has one name.
    const list: MenuItem[] = [
      {
        id: 'profile',
        icon: 'mat:account_circle',
        label: 'shared.userMenu.accountSettings',
        description: 'shared.toolbarUserDropdown.items.profile.description',
        colorClass: 'text-teal-600',
        route: '/apps/user-profile'
      },
      {
        id: 'notifications',
        icon: 'mat:notifications',
        label: 'shared.notifications.title',
        description: 'shared.toolbarUserDropdown.items.notifications.description',
        colorClass: 'text-primary-600',
        route: '/apps/user-profile',
        queryParams: { tab: 'notifications' }
      }
    ];

    if (u && (u.hasRole('superadmin') || u.hasRole('admin'))) {
      list.push({
        id: 'admin',
        icon: 'mat:admin_panel_settings',
        label: 'shared.toolbarUserDropdown.items.admin.label',
        description: 'shared.toolbarUserDropdown.items.admin.description',
        colorClass: 'text-red-600',
        route: '/admin'
      });
    }

    return list;
  });

  close(): void {
    this.popoverRef.close();
  }

  goToAdmin(): void {
    this.close();
    void this.router.navigate(['/admin']).then(() => {
      this.contextualSidebarService.showAdminSidebar();
      this.vexLayoutService.openContextualSidenav();
    });
  }

  async logout(): Promise<void> {
    try {
      await this.authService.logout();
      this.close();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }
}
